import React from 'react';
import { RootStore } from '~stores/root';

// No component is exported here: that would add a React Refresh boundary these
// hooks can never satisfy, which downgrades the hot-reload to a full page
// reload. Only the context is kept across hot-reloads, so the provider and its
// consumers share one identity; the store is rebuilt, or its instances would
// keep running the code they were built from.
type HotState = { storeContext?: React.Context<RootStore | null> };

const hotState = import.meta.hot?.data as HotState | undefined;

export const rootStore = new RootStore();
export const storeContext: React.Context<RootStore | null> =
    hotState?.storeContext ?? React.createContext<RootStore | null>(null);

if (hotState) {
    hotState.storeContext = storeContext;
}

export const useStore = () => {
    const store = React.useContext(storeContext);
    if (!store) {
        throw new Error('useStore must be used within a StoreProvider.');
    }
    return store;
};

export const useAuthStore = () => {
    const store = useStore();
    return store.authStore;
};

export const useUserStore = () => {
    const store = useStore();
    return store.userStore;
};

export const useConfigStore = () => {
    const store = useStore();
    return store.configStore;
};

export const usePromotionStore = () => {
    const store = useStore();
    return store.promotionStore;
};

export const useCategoryStore = () => {
    const store = useStore();
    return store.categoryStore;
};

export const useOfferStore = () => {
    const store = useStore();
    return store.offerStore;
};

export const useFeaturedStore = () => {
    const store = useStore();
    return store.featuredStore;
};

export const useClientStore = () => {
    const store = useStore();
    return store.clientStore;
};

export const usePromotionGrantStore = () => {
    const store = useStore();
    return store.promotionGrantStore;
};

export const useFeedbackStore = () => {
    const store = useStore();
    return store.feedbackStore;
};

export const useFeedbackNPSStore = () => {
    const store = useStore();
    return store.npsStore;
};

export const useStoresStore = () => {
    const store = useStore();
    return store.storesStore;
};

export const useGamesStore = () => {
    const store = useStore();
    return store.gamesStore;
};

export const useAnalyticsStore = () => {
    const store = useStore();
    return store.AnalyticsStore;
};

export const useNhlGamesStore = () => {
    const store = useStore();
    return store.nhlGamesStore;
};

export const useAcvtStore = () => {
    const store = useStore();
    return store.acvtStore;
};

export const useCareerStore = () => {
    const store = useStore();
    return store.careerStore;
};
export const useRechargeStore = () => {
    const store = useStore();
    return store.rechargeStore;
};

export const useRppAdStore = () => {
    const store = useStore();
    return store.rppAdStore;
};

export const useRppPrizeCouponStore = () => {
    const store = useStore();
    return store.rppPrizeCouponStore;
};

export const useContestStore = () => {
    const store = useStore();
    return store.contestStore;
};

export const useDoc31Store = () => {
    const store = useStore();
    return store.doc31Store;
};

export const useCarWashStore = () => {
    const store = useStore();
    return store.carWashStore;
};

export const useOnboardingStore = () => {
    const store = useStore();
    return store.onboardingStore;
};

export const useLabattGameStore = () => {
    const store = useStore();
    return store.labattGameStore;
};

export const useEventStore = () => {
    const store = useStore();
    return store.eventStore;
};

export const useBusinessUnitStore = () => {
    const store = useStore();
    return store.businessUnitStore;
};

export const useApplicationSelectionStore = () => {
    const store = useStore();
    return store.applicationSelectionStore;
};

export const useSectionPlacementsStore = () => {
    const store = useStore();
    return store.sectionPlacementsStore;
};

export const useLoyaltyBannerStore = () => {
    const store = useStore();
    return store.loyaltyBannerStore;
};

export const useLoyaltyClubStore = () => {
    const store = useStore();
    return store.loyaltyClubStore;
};
